import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import {
  districts,
  states,
  stars,
  raasis,
  maritalStatusOptions,
  idProofTypes,
  panCardOptions,
  roleOptions,
} from "../utils/formDataUtils";
import { useMutation } from "@apollo/client";
import { EDIT_MEMBER_MUTATION } from "../graphql/mutations";
import { Snackbar, Alert } from "@mui/material";
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

const EditUserModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [editMember] = useMutation(EDIT_MEMBER_MUTATION);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const storedUserType = localStorage.getItem("user_type");
  const [submittedFields, setSubmittedFields] = useState({});

  const paymentStatusOptions = [
    { value: "Unpaid", label: "Unpaid" },
    { value: "Paid", label: "Paid" }
  ];


  // Define non-required fields
  const nonRequiredFields = ["Star", "Raasi", "Kootram", "Kuladeivam", "Pan_Number", "Email", "id"];

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (isOpen && user) {
      // Map database field names to form field names and process data
      const processedUser = {
        ...user,
        Transaction_Reference_ID: user.Payment_Id,
        Transaction_Date_Time: user.Payment_Date,
        Payment_Status: user.Payment_Status || "Unpaid",
        Payment_Method: user.Payment_Method || null
      };

      // Extend dayjs with the plugins
      dayjs.extend(utc);
      dayjs.extend(timezone);

      if (processedUser.Transaction_Date_Time) {
        const date = dayjs(processedUser.Transaction_Date_Time);

        if (date.isValid()) {
          // Convert to IST
          const istDate = date.tz('Asia/Kolkata');

          // Format the date to a string suitable for datetime-local input
          processedUser.Transaction_Date_Time = istDate.format('YYYY-MM-DDTHH:mm');
        } else {
          // Optionally clear or ignore invalid input
          processedUser.Transaction_Date_Time = "";
        }
      }

      setFormData(processedUser);
      setFormErrors({});

      // Initialize submittedFields based on existing user data
      const fieldsSubmitted = {};

      // If user already has Pan Card as "Yes" and a valid Pan Number
      if (processedUser.Pan_Card === "Yes" && processedUser.Pan_Number && processedUser.Pan_Number.trim() !== "") {
        fieldsSubmitted.Pan_Card = true;
        fieldsSubmitted.Pan_Number = true;
      }

      // If payment status is "paid" in DB, disable the dropdown
      if (user.Payment_Status === "Paid") {
        fieldsSubmitted.Payment_Status = true;
      }

      setSubmittedFields(fieldsSubmitted);
    }
  }, [isOpen, user?.id]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    // Force uppercase for PAN and ID numbers
    if (name === "Pan_Number" || name === "ID_Proof_Number") {
      updatedValue = value.toUpperCase();
    }

    // Only alphabets for occupation
    if (name === "Occupation") {
      updatedValue = value.replace(/[^a-zA-Z\s]/g, "");
    }

    if (name === "Kootram" || name === "Kuladeivam") {
      updatedValue = value.replace(/[^a-zA-Z\s]/g, ""); // allow letters and spaces only
    }

    // Family details: alphabets, numbers, spaces, and comma
    if (name === "Family_Details") {
      updatedValue = value.replace(/[^a-zA-Z0-9,\s]/g, "");
    }

    // Address: alphabets, numbers, comma, slash and space
    if (name === "Address") {
      updatedValue = value.replace(/[^a-zA-Z0-9,\/\s]/g, "");
    }

    // Special handling for Pan_Card field
    if (name === "Pan_Card") {
      if (value === "No") {
        // If "No" is selected, clear the Pan_Number field
        setFormData((prev) => ({
          ...prev,
          [name]: updatedValue,
          Pan_Number: "" // Clear PAN number when "No" is selected
        }));
        return;
      }
    }

    if (name === "Payment_Status") {
      if (value === "Unpaid") {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          Transaction_Reference_ID: "",
          Transaction_Date_Time: "",
          Payment_Method: null // Clear payment method when unpaid
        }));
        return;
      } else if (value === "Paid") {
        // Set payment method to UPI when paid is selected
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          Payment_Method: "upi"
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const errors = {};

    // Get all visible fields
    const fieldsToValidate = storedUserType === "superuser" ? [...fields, "Role"] : fields;

    // Validate required fields
    fieldsToValidate.forEach(field => {
      // Skip validation for non-required fields and conditional fields
      if (nonRequiredFields.includes(field)) {
        return;
      }

      // Skip Pan_Number validation if Pan_Card is not "Yes"
      if (field === "Pan_Number" && formData.Pan_Card !== "Yes") {
        return;
      }

      // Skip ID_Proof_Number validation if ID_Proof_Type is not selected
      if (field === "ID_Proof_Number" && !formData.ID_Proof_Type) {
        return;
      }

      // Skip payment fields validation if user is not superuser
      if ((field === "Transaction_Reference_ID" || field === "Transaction_Date_Time") && storedUserType !== "superuser") {
        return;
      }

      // Skip payment fields validation if Payment_Status is not "Paid"
      if ((field === "Transaction_Reference_ID" || field === "Transaction_Date_Time") && formData.Payment_Status !== "Paid") {
        return;
      }

      // Check if field is empty or only whitespace
      const fieldValue = formData[field];
      if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
        const label =
          field === "id"
            ? "ID"
            : field === "Kootram"
              ? "Gothram"
              : field === "Transaction_Reference_ID"
                ? "Transaction / Reference ID"
                : field === "Transaction_Date_Time"
                  ? "Transaction Date & Time"
                  : field.replaceAll("_", " ");
        errors[field] = `${label} is required`;
      }
    });

    // Payment Status specific validations - only for superuser
    if (storedUserType === "superuser" && formData.Payment_Status === "Paid") {
      if (!formData.Transaction_Reference_ID?.trim()) {
        errors.Transaction_Reference_ID = "Transaction / Reference ID is required";
      }

      if (!formData.Transaction_Date_Time?.trim()) {
        errors.Transaction_Date_Time = "Transaction Date & Time is required";
      } else {
        const minValidDate = new Date("1900-01-01T00:00:00Z");
        const inputDate = new Date(formData.Transaction_Date_Time);

        if (isNaN(inputDate.getTime()) || inputDate < minValidDate) {
          errors.Transaction_Date_Time = "Transaction Date & Time must be after 01-01-1900";
        }
      }
    }

    // Existing specific validations
    if (formData.Pan_Card === "Yes" && !formData.Pan_Number?.trim()) {
      errors.Pan_Number = "PAN Number is required";
    }

    if (formData.ID_Proof_Type && !formData.ID_Proof_Number?.trim()) {
      errors.ID_Proof_Number = "ID Proof Number is required";
    }

    // Add additional validation for ID Proof Number format
    if (formData.ID_Proof_Type && formData.ID_Proof_Number) {
      const idProofType = formData.ID_Proof_Type.trim().toLowerCase();
      const idProofNumber = formData.ID_Proof_Number.trim();

      if (idProofType === "aadhaar") {
        if (!/^\d{12}$/.test(idProofNumber)) {
          errors.ID_Proof_Number = "Aadhaar number must be exactly 12 digits";
        }
      } else if (idProofType === "license") {
        if (idProofNumber.length !== 15) {
          errors.ID_Proof_Number = "License number must be exactly 15 characters";
        }
      }
    }

    // Add additional validation for PAN Number format
    if (formData.Pan_Card === "Yes" && formData.Pan_Number) {
      // Simple validation for PAN (10 alphanumeric characters)
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.Pan_Number)) {
        errors.Pan_Number = "Invalid PAN format (e.g., ABCDE1234F)";
      }
    }

    // Add additional validation for Transaction/Reference ID format
    if (formData.Transaction_Reference_ID && formData.Transaction_Reference_ID.trim()) {
      if (!/^[a-zA-Z0-9\-_.]+$/.test(formData.Transaction_Reference_ID)) {
        errors.Transaction_Reference_ID =
          "Transaction / Reference ID can only contain letters, numbers, dashes, underscores, and dots";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    window.scrollTo(0, 0);
    if (!validateForm()) return;


    try {
      const formattedDob = new Date(formData.DOB).toLocaleDateString("en-CA");

      const isPaymentDone = formData.Payment_Status === "Paid";

      // Prepare submission data - if Pan_Card is "No", don't include Pan_Number
      const submissionData = {
        id: formData.id,
        First_Name: formData.First_Name,
        Last_Name: formData.Last_Name,
        Mobile_Number: formData.Mobile_Number,
        DOB: formattedDob,
        City: formData.City,
        District: formData.District,
        State: formData.State,
        Pincode: formData.Pincode,
        Email: formData.Email,
        Occupation: formData.Occupation,
        Role: formData.Role,
        Address: formData.Address,
        Marital_Status: formData.Marital_Status,
        ID_Proof_Type: formData.ID_Proof_Type,
        ID_Proof_Number: formData.ID_Proof_Number,
        Pan_Card: formData.Pan_Card,
        Star: formData.Star,
        Raasi: formData.Raasi,
        Kootram: formData.Kootram,
        Kuladeivam: formData.Kuladeivam,
        Family_Details: formData.Family_Details,
        Payment_Status: formData.Payment_Status,
        Payment_Id: isPaymentDone ? formData.Transaction_Reference_ID : null,
        Payment_Date: isPaymentDone ? formData.Transaction_Date_Time : null,
        Payment_Method: isPaymentDone ? "upi" : null,
      };

      // Only include Pan_Number if Pan_Card is "Yes"
      if (formData.Pan_Card === "Yes") {
        submissionData.Pan_Number = formData.Pan_Number;
      } else {
        submissionData.Pan_Number = ""; // Always send empty string if Pan_Card is not "Yes"
      }

      const { data: mutationData } = await editMember({
        variables: submissionData
      });

      if (mutationData?.editMember) {
        // Update submitted fields after successful submission
        // This will track that the Pan Card was submitted with valid values
        if (formData.Pan_Card === "Yes" && formData.Pan_Number && formData.Pan_Number.trim() !== "") {
          setSubmittedFields(prev => ({
            ...prev,
            Pan_Card: true,
            Pan_Number: true
          }));
          setIsFormSubmitted(true);
        }

        setSnackbarMessage("Member updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        onClose();
        onSuccess?.();
      }
    } catch (error) {
      const apiMessage =
        error?.graphQLErrors?.[0]?.message || "Failed to update member.";
      setSnackbarMessage(apiMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const getFieldInput = (field) => {
    let maxLength;

    if (field === "ID_Proof_Number") {
      const idType = (formData.ID_Proof_Type || "").trim().toLowerCase();
      if (idType === "aadhaar") maxLength = 12;
      if (idType === "license") maxLength = 15;
    }

    if (field === "Pan_Number") {
      maxLength = 10;
    }

    if (field === "Transaction_Reference_ID") {
      maxLength = 50;
    }

    const dropdownMap = {
      District: districts,
      State: states,
      Star: stars,
      Raasi: raasis,
      Marital_Status: maritalStatusOptions,
      ID_Proof_Type: idProofTypes,
      Pan_Card: panCardOptions,
      Role: roleOptions,
      Payment_Status: paymentStatusOptions,
    };

    const label =
      field === "id"
        ? "ID"
        : field === "Kootram"
          ? "Gothram"
          : field === "Transaction_Reference_ID"
            ? "Transaction / Reference ID"
            : field === "Transaction_Date_Time"
              ? "Transaction Date & Time"
              : field.replaceAll("_", " ");
    const isDropdown = Object.keys(dropdownMap).includes(field);
    const isReadOnly = field === "Mobile_Number" || field === "DOB";
    const isRequired = !nonRequiredFields.includes(field);

    // Fix the disabled logic - only disable ID field
    // Disable logic for the Pan Card dropdown
    const isPanCardDisabled = field === "Pan_Card" &&
      submittedFields.Pan_Card && // Check if previously submitted
      formData.Pan_Card === "Yes" &&
      formData.Pan_Number &&
      formData.Pan_Number.trim() !== "";

    // Disable logic for Payment_Status - disable after successful submission
    const isPaymentStatusDisabled = field === "Payment_Status" && submittedFields.Payment_Status;

    // General disable logic
    const isDisabled = field === "id" || isPanCardDisabled || isPaymentStatusDisabled;

    return (
      <div key={field} className="flex flex-col space-y-1">
        <label
          htmlFor={field}
          className="text-sm font-medium text-gray-700 text-left"
        >
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>

        {isDropdown ? (
          <select
            name={field}
            id={field}
            value={formData[field] || ""}
            onChange={handleOnChange}
            disabled={isDisabled}
            required={isRequired}
            className={`mt-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${isDisabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
              } ${formErrors[field] ? "border-red-500" : ""}`}
          >
            <option value="">Select {label}</option>
            {dropdownMap[field].map((option) => {
              const value = typeof option === "string" ? option : option.value;
              const labelText = typeof option === "string" ? option : option.label;
              return (
                <option key={value} value={value}>
                  {labelText}
                </option>
              );
            })}
          </select>
        ) : field === "Transaction_Date_Time" ? (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              value={formData[field] ? dayjs(formData[field]) : null}
              onChange={(newValue) => {
                if (dayjs(newValue).isValid()) {
                  handleOnChange({
                    target: {
                      name: field,
                      value: newValue ? dayjs(newValue).toISOString() : "",
                    },
                  });
                }
              }}
              timeSteps={{ minutes: 1 }}
              disabled={isDisabled}
              readOnly={isReadOnly}
              minDate={dayjs("1900-01-01")}
              maxDateTime={dayjs()}
              format="YYYY-MM-DD HH:mm"
              slotProps={{
                textField: {
                  // required: isRequired,
                  // error: Boolean(formErrors[field]),
                  // helperText: formErrors[field],
                  fullWidth: true,
                  variant: "outlined",
                  InputProps: {
                    disableUnderline: true,
                    sx: {
                      backgroundColor: isDisabled || isReadOnly ? "#f3f4f6" : "#fff",
                      color: isDisabled || isReadOnly ? "#6b7280" : "#111827",
                      borderRadius: "0.375rem",
                      fontSize: "0.875rem",
                      height: "42px",
                      padding: 0,
                      border: formErrors[field]
                        ? "1px solid #ef4444"
                        : "1px solid #d1d5db",
                      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)", // Matches the soft shadow
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none", // Remove default outline
                      },
                      "& .MuiInputBase-input": {
                        padding: "0.5rem 0.75rem",
                        fontSize: "1.0rem",
                        fontWeight: 400,
                        fontFamily: "inherit",
                        lineHeight: "1.25rem",
                      },
                      "& .MuiInputAdornment-root": {
                        marginRight: "0.5rem",
                        alignItems: "center",
                        display: "flex",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "1.25rem",
                      },
                      "&:hover": {
                        borderColor: formErrors[field] ? "#ef4444" : "#d1d5db",
                        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                      },
                      "&.Mui-focused": {
                        borderColor: "#facc15",
                        boxShadow: "0 0 0 2px #fde68a",
                      },
                      "&:focus": {
                        outline: "none",
                      },
                    },
                  },

                },
              }}

            />
          </LocalizationProvider>
        ) : (
          <input
            type="text"
            name={field}
            id={field}
            value={formData[field] || ""}
            onChange={handleOnChange}
            readOnly={isReadOnly}
            disabled={isDisabled}
            required={isRequired}
            maxLength={maxLength}
            className={`mt-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${isReadOnly || isDisabled
              ? "bg-gray-100 cursor-not-allowed text-gray-500"
              : ""
              } ${formErrors[field] ? "border-red-500" : ""}`}
          />
        )}
        {formErrors[field] && (
          <p className="text-sm text-red-500 text-left pl-1">{formErrors[field]}</p>
        )}
      </div>
    );
  };

  const fields = [
    "id",
    "First_Name",
    "Last_Name",
    "Mobile_Number",
    "Email",
    "DOB",
    "Occupation",
    "Address",
    "City",
    "District",
    "State",
    "Pincode",
    "Marital_Status",
    "Family_Details",
    "ID_Proof_Type",
    "ID_Proof_Number",
    "Pan_Card",
    "Pan_Number",
    "Star",
    "Raasi",
    "Kootram",
    "Kuladeivam",
    // Only include payment fields for superuser
    ...(storedUserType === "superuser" ? ["Payment_Status", "Transaction_Reference_ID", "Transaction_Date_Time"] : []),
  ];

  // Conditionally add "Role" field only for superuser
  const visibleFields = storedUserType === "superuser" ? [...fields, "Role"] : fields;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 right-0 w-full max-w-xl pt-24 h-full shadow-xl z-50 overflow-y-auto bg-[#F8F9E9]"
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">Edit Member Profile</h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-800"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5 bg-[#F8F9E9]">
              {visibleFields.map((field) => {
                if (field === "Pan_Number" && formData.Pan_Card !== "Yes") {
                  return null;
                }
                if (field === "ID_Proof_Number" && !formData.ID_Proof_Type) {
                  return null;
                }
                // Add these conditions for payment fields - only show for superuser and when paid
                if ((field === "Transaction_Reference_ID" || field === "Transaction_Date_Time") &&
                  (storedUserType !== "superuser" || formData.Payment_Status !== "Paid")) {
                  return null;
                }
                return getFieldInput(field);
              })}
            </div>

            <div className="p-4 border-t flex justify-end gap-4 bg-[#F8F9E9]">
              <button
                onClick={onClose}
                style={{ backgroundColor: '#E52029', borderRadius: '999px' }}
                className="px-4 py-2 text-white hover:opacity-90"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                style={{ backgroundColor: "green", borderRadius: '999px' }}
                className="px-4 py-2 text-white hover:opacity-90"
              >
                Save Changes
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditUserModal;